// Server-enforced finish (bundle K).
//
// Interview 61: Alex said "We've covered everything I had planned" with ~29 minutes left (reserve problem
// never asked), then kept answering after its closing until the page disconnected. Now:
//   - Alex starts to finish or close → the backend's /finish-check decides. Not allowed → the page sends
//     Alex the backend's instruction (ask the verification round / reserve problem now).
//   - Allowed (or the wrap-up period has begun) and Alex says its closing line → the page ends the call
//     itself once Alex has been quiet for END_AFTER_QUIET_MS (never the end_call tool: the model decides that).
// Plain refs/timers only — no React state during the call.

// Must match the backend prompt: phase3_interview_engine.END_INTERVIEW_LINE and the honest-finish line
export const CLOSING_MARKER = 'click end interview';
export const FINISH_MARKER = 'covered everything i had planned';
export const END_AFTER_QUIET_MS = 3000;
export const END_MAX_WAIT_MS = 60000;
export const POLL_MS = 250;

// Whitespace-insensitive: ElevenLabs transcripts can split words ("fe edback", "discuss ed")
const squash = (t) => String(t || '').toLowerCase().replace(/[’']/g, "'").replace(/\s+/g, '');

export function classifyAgentText(text) {
  const t = squash(text);
  return { finishAttempt: t.includes(squash(FINISH_MARKER)), closing: t.includes(squash(CLOSING_MARKER)) };
}

/**
 * check(payload) → Promise<{allow, instruction}> (the /finish-check call); sendInstruction(text);
 * endCall(reason); isAgentSpeaking(); elapsedSeconds(); wrapUpStarted() — the page's T-5 message was sent.
 */
export function createFinishFlow({ check, sendInstruction, endCall, isAgentSpeaking, elapsedSeconds,
  wrapUpStarted = () => false, now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout }) {
  const agentTexts = [];
  const userTexts = [];
  let allowed = false;
  let checking = false;
  let closingSeen = false;
  let ending = false;
  let timer = null;

  const scheduleEnd = () => {
    if (ending) return;
    ending = true;
    const started = now();
    let quietSince = null;
    const poll = () => {
      const t = now();
      if (isAgentSpeaking()) quietSince = null; else if (quietSince === null) quietSince = t;
      if ((quietSince !== null && t - quietSince >= END_AFTER_QUIET_MS) || t - started >= END_MAX_WAIT_MS) {
        timer = null;
        endCall('agent_closed');
        return;
      }
      timer = setTimer(poll, POLL_MS);
    };
    timer = setTimer(poll, POLL_MS);
  };

  const runCheck = async () => {
    checking = true;
    let res = null;
    try {
      res = await check({ elapsed_seconds: elapsedSeconds(), agent_messages: agentTexts.slice(-80),
                          user_messages: userTexts.slice(-5) });
    } catch (e) { res = null; }
    checking = false;
    // Fail open: if the server can't answer, behave as before this bundle (Alex may finish)
    if (!res || res.allow !== false) {
      allowed = true;
      if (closingSeen) scheduleEnd();
    } else if (res.instruction) {
      closingSeen = false;
      try { sendInstruction(res.instruction); } catch (e) { /* ignore */ }
    }
  };

  return {
    onAgentText(text) {
      if (!text) return;
      agentTexts.push(String(text));
      const c = classifyAgentText(text);
      if (!c.finishAttempt && !c.closing) return;
      if (c.closing) closingSeen = true;
      if (allowed || wrapUpStarted()) {
        allowed = true;
        if (closingSeen) scheduleEnd();
        return;
      }
      if (!checking) runCheck();
    },
    onUserText(text) { if (text) userTexts.push(String(text)); },
    stop() { if (timer) clearTimer(timer); timer = null; ending = true; },
    state: () => ({ allowed, checking, closingSeen, ending }),
  };
}
